import { createMuiTheme } from '@material-ui/core/styles';

const getTheme = (mode) => createMuiTheme({
  palette: {
    type: mode,
    primary: {
      main: '#10b981',
      light: '#34d399',
      dark: '#059669',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#d4a853',
      light: '#e8c97a',
      dark: '#b8892e',
      contrastText: '#ffffff',
    },
    background: {
      default: mode === 'dark' ? '#0f172a' : '#f5f0eb',
      paper: mode === 'dark' ? '#1e293b' : '#fcf9f5',
    },
    error: { main: '#ef4444', light: '#f87171' },
    warning: { main: '#f59e0b' },
    success: { main: '#10b981' },
    text: {
      primary: mode === 'dark' ? '#f1f5f9' : '#2d2a24',
      secondary: mode === 'dark' ? '#94a3b8' : '#6b6560',
      disabled: mode === 'dark' ? '#475569' : '#a09890',
    },
    divider: mode === 'dark' ? 'rgba(148,163,184,0.12)' : 'rgba(45,42,36,0.08)',
    action: {
      hover: mode === 'dark' ? 'rgba(148,163,184,0.06)' : 'rgba(16,185,129,0.06)',
      selected: mode === 'dark' ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.08)',
    },
  },
  typography: {
    fontFamily: '"Inter", "Plus Jakarta Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 800, letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, letterSpacing: '-0.01em' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700, letterSpacing: '-0.01em' },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 500 },
    subtitle2: { fontWeight: 600 },
    body1: { fontWeight: 400 },
    body2: { fontWeight: 400 },
    button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.01em' },
  },
  shape: { borderRadius: 12 },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '*': {
          boxSizing: 'border-box',
          scrollbarWidth: 'thin',
          scrollbarColor: mode === 'dark' ? '#334155 transparent' : '#cbc5be transparent',
        },
        '*::-webkit-scrollbar': { width: 6, height: 6 },
        '*::-webkit-scrollbar-track': { background: 'transparent' },
        '*::-webkit-scrollbar-thumb': {
          background: mode === 'dark' ? '#334155' : '#cbc5be',
          borderRadius: 3,
        },
        body: {
          backgroundColor: mode === 'dark' ? '#0f172a' : '#f5f0eb',
          backgroundImage:
            mode === 'dark'
              ? 'radial-gradient(ellipse at 20% 50%, rgba(16,185,129,0.04) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(212,168,83,0.04) 0%, transparent 50%)'
              : 'radial-gradient(ellipse at 20% 50%, rgba(16,185,129,0.03) 0%, transparent 60%), radial-gradient(ellipse at 80% 20%, rgba(212,168,83,0.03) 0%, transparent 50%)',
          backgroundAttachment: 'fixed',
        },
        // Universal button spacing so no two buttons are glued together anywhere
        '.MuiButton-root:not(.MuiButtonGroup-grouped) + .MuiButton-root:not(.MuiButtonGroup-grouped)': {
          marginLeft: '12px !important',
        },
        '.MuiButton-root + .MuiIconButton-root, .MuiIconButton-root + .MuiButton-root': {
          marginLeft: '8px !important',
        },
        '.MuiIconButton-root + .MuiIconButton-root': {
          marginLeft: '6px !important',
        },
        '.MuiChip-root + .MuiChip-root': {
          marginLeft: '6px',
        },
        '.MuiDialogActions-root': {
          padding: '16px 24px',
          gap: 12,
          '& > .MuiButton-root:not(:first-child)': {
            marginLeft: '12px !important',
          },
        },
      },
    },
    MuiAppBar: {
      root: {
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        background: mode === 'dark'
          ? 'rgba(15,23,42,0.85)'
          : 'rgba(245,240,235,0.85)',
        borderBottom: mode === 'dark'
          ? '1px solid rgba(148,163,184,0.1)'
          : '1px solid rgba(45,42,36,0.06)',
        boxShadow: mode === 'dark'
          ? '0 1px 0 rgba(148,163,184,0.08)'
          : '0 1px 0 rgba(45,42,36,0.04)',
      },
    },
    MuiCard: {
      root: {
        borderRadius: 16,
        background: mode === 'dark' ? '#1e293b' : '#ffffff',
        border: mode === 'dark'
          ? '1px solid rgba(148,163,184,0.08)'
          : '1px solid rgba(45,42,36,0.06)',
        boxShadow: mode === 'dark'
          ? '0 4px 24px rgba(0,0,0,0.3)'
          : '0 2px 12px rgba(45,42,36,0.06)',
        transition: 'all 0.25s ease',
      },
    },
    MuiPaper: {
      root: {
        background: mode === 'dark' ? '#1e293b' : '#ffffff',
        border: mode === 'dark'
          ? '1px solid rgba(148,163,184,0.08)'
          : '1px solid rgba(45,42,36,0.06)',
      },
      rounded: { borderRadius: 16 },
      elevation0: { boxShadow: 'none' },
      elevation1: {
        boxShadow: mode === 'dark'
          ? '0 4px 24px rgba(0,0,0,0.3)'
          : '0 2px 12px rgba(45,42,36,0.06)',
      },
    },
    MuiButton: {
      root: {
        borderRadius: 10,
        padding: '8px 20px',
        fontSize: '0.875rem',
        fontWeight: 600,
        transition: 'all 0.2s ease',
      },
      containedPrimary: {
        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        boxShadow: '0 4px 15px rgba(16,185,129,0.3)',
        '&:hover': {
          background: 'linear-gradient(135deg, #34d399 0%, #10b981 100%)',
          boxShadow: '0 6px 20px rgba(16,185,129,0.4)',
          transform: 'translateY(-1px)',
        },
      },
      containedSecondary: {
        background: 'linear-gradient(135deg, #d4a853 0%, #b8892e 100%)',
        boxShadow: '0 4px 15px rgba(212,168,83,0.3)',
        '&:hover': {
          boxShadow: '0 6px 20px rgba(212,168,83,0.4)',
          transform: 'translateY(-1px)',
        },
      },
      outlinedPrimary: {
        borderColor: mode === 'dark'
          ? 'rgba(16,185,129,0.4)'
          : 'rgba(16,185,129,0.5)',
        color: '#10b981',
        '&:hover': {
          background: 'rgba(16,185,129,0.08)',
          borderColor: '#10b981',
        },
      },
    },
    MuiTableHead: {
      root: {
        '& .MuiTableCell-head': {
          background: mode === 'dark'
            ? 'rgba(16,185,129,0.08)'
            : 'rgba(16,185,129,0.06)',
          color: '#10b981',
          fontWeight: 700,
          fontSize: '0.75rem',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          borderBottom: mode === 'dark'
            ? '1px solid rgba(16,185,129,0.2)'
            : '1px solid rgba(16,185,129,0.15)',
        },
      },
    },
    MuiTableBody: {
      root: {
        '& .MuiTableRow-root': {
          transition: 'background 0.15s ease',
          '&:hover': {
            background: mode === 'dark'
              ? 'rgba(16,185,129,0.04)'
              : 'rgba(16,185,129,0.03)',
          },
        },
        '& .MuiTableCell-body': {
          borderBottom: mode === 'dark'
            ? '1px solid rgba(148,163,184,0.06)'
            : '1px solid rgba(45,42,36,0.04)',
          color: mode === 'dark' ? '#e2e8f0' : '#3d3a35',
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottom: mode === 'dark'
          ? '1px solid rgba(148,163,184,0.08)'
          : '1px solid rgba(45,42,36,0.06)',
        padding: '14px 16px',
      },
    },
    MuiInputBase: {
      root: {
        borderRadius: 10,
        backgroundColor: mode === 'dark'
          ? 'rgba(15,23,42,0.6)'
          : 'rgba(255,255,255,0.8)',
        color: mode === 'dark' ? '#f1f5f9' : '#2d2a24',
      },
    },
    MuiOutlinedInput: {
      root: {
        borderRadius: 10,
        '& fieldset': {
          borderColor: mode === 'dark'
            ? 'rgba(148,163,184,0.2)'
            : 'rgba(45,42,36,0.15)',
        },
        '&:hover fieldset': {
          borderColor: mode === 'dark'
            ? 'rgba(16,185,129,0.4)'
            : 'rgba(16,185,129,0.5)',
        },
        '&.Mui-focused fieldset': {
          borderColor: '#10b981',
          borderWidth: 1.5,
        },
      },
    },
    MuiInputLabel: {
      root: { color: mode === 'dark' ? '#94a3b8' : '#6b6560' },
      focused: { color: '#10b981 !important' },
    },
    MuiChip: {
      root: {
        borderRadius: 8,
        fontWeight: 600,
        fontSize: '0.75rem',
      },
    },
    MuiDivider: {
      root: {
        backgroundColor: mode === 'dark'
          ? 'rgba(148,163,184,0.1)'
          : 'rgba(45,42,36,0.08)',
      },
    },
    MuiDialog: {
      paper: {
        background: mode === 'dark' ? '#1e293b' : '#ffffff',
        border: mode === 'dark'
          ? '1px solid rgba(148,163,184,0.12)'
          : '1px solid rgba(45,42,36,0.08)',
        borderRadius: 20,
        boxShadow: mode === 'dark'
          ? '0 25px 50px rgba(0,0,0,0.5)'
          : '0 25px 50px rgba(45,42,36,0.1)',
      },
    },
    MuiDialogTitle: {
      root: {
        paddingBottom: 8,
        '& h2': {
          fontWeight: 700,
          color: mode === 'dark' ? '#f1f5f9' : '#2d2a24',
        },
      },
    },
    MuiLinearProgress: {
      root: {
        borderRadius: 4,
        backgroundColor: 'rgba(16,185,129,0.15)',
        height: 6,
      },
      barColorPrimary: {
        background: 'linear-gradient(90deg, #10b981, #059669)',
        borderRadius: 4,
      },
    },
    MuiCircularProgress: {
      colorPrimary: { color: '#10b981' },
    },
    MuiSnackbar: {
      root: {
        '& .MuiSnackbarContent-root': {
          background: mode === 'dark' ? '#334155' : '#2d2a24',
          color: mode === 'dark' ? '#f1f5f9' : '#f5f0eb',
          fontWeight: 500,
          borderRadius: 12,
          boxShadow: mode === 'dark'
            ? '0 8px 32px rgba(0,0,0,0.4)'
            : '0 8px 32px rgba(45,42,36,0.12)',
        },
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: 10,
        '&:hover': {
          background: mode === 'dark'
            ? 'rgba(148,163,184,0.08)'
            : 'rgba(16,185,129,0.06)',
        },
      },
    },
    MuiTooltip: {
      tooltip: {
        background: mode === 'dark' ? '#334155' : '#2d2a24',
        color: mode === 'dark' ? '#f1f5f9' : '#f5f0eb',
        fontSize: '0.75rem',
        borderRadius: 8,
        border: mode === 'dark'
          ? '1px solid rgba(148,163,184,0.12)'
          : '1px solid rgba(45,42,36,0.1)',
      },
    },
    MuiSelect: {
      icon: {
        color: mode === 'dark' ? '#94a3b8' : '#6b6560',
      },
    },
  },
});

export default getTheme;
